import React, { createContext, useContext, ReactNode } from 'react';
import { INaviTable, UseNaviTableProps } from '../interfaces';
import { RowData } from '@tanstack/react-table';

export type INaviTableContextValue<TData extends RowData> = INaviTable<TData> &
  UseNaviTableProps<TData>;
interface INaviTableContext<TData extends RowData> {
  children: ReactNode;
  value: INaviTableContextValue<TData>;
}

function createNaviTableContext<TData extends RowData>(): {
  NaviTableContext: React.Context<INaviTable<TData> | undefined>;
  NaviTableProvider: React.FC<INaviTableContext<TData>>;
} {
  const NaviTableContext = createContext<INaviTable<TData> | undefined>(undefined);

  const NaviTableProvider: React.FC<INaviTableContext<TData>> = ({ children, value }) => {
    return <NaviTableContext.Provider value={value}>{children}</NaviTableContext.Provider>;
  };

  return { NaviTableContext, NaviTableProvider };
}

const { NaviTableContext, NaviTableProvider } = createNaviTableContext<RowData>();

export const useNaviTableContext = <TData extends RowData>() => {
  const context = useContext(
    NaviTableContext as React.Context<(INaviTable<TData> & UseNaviTableProps<TData>) | undefined>
  );
  if (!context) {
    throw new Error('useNaviTableContext must be used within a NaviTableProvider');
  }
  return context;
};

export { NaviTableProvider };
